
import {
  ASSIGNMENT_PERMISSION_ERROR_MESSAGE,
  createDefaultCtaList,
  IN_APP_NOTIFICATION_ALLOWED_HTML_TAGS,
  IN_APP_NOTIFICATION_CTA_TARGET_PREFIX,
  IN_APP_NOTIFICATION_VALIDATIONS,
  PLATFORM_OPTIONS,
} from "../constants/inAppNotificationConstant";
import { addDays, endOfDay, format, startOfDay } from "date-fns";
import {
  decodePlainTextFromApi,
  decodeRichTextHtmlFromApi,
  encodeTextForApi,
} from "./base64Utils";

const VALID_PLATFORM_VALUES = new Set(PLATFORM_OPTIONS.map((option) => option.value));

export const normalizePlatformValues = (platform) => {
  if (!platform) return [];

  const values = Array.isArray(platform) ? platform : [platform];
  return values
    .map((item) => {
      const raw = String(item ?? "")
        .trim()
        .toLowerCase();
      if (raw === "android") return "an";
      if (VALID_PLATFORM_VALUES.has(raw)) return raw;
      return null;
    })
    .filter(Boolean);
};

const ALLOWED_HTML_TAG_SET = new Set(IN_APP_NOTIFICATION_ALLOWED_HTML_TAGS);
const ALLOWED_TAG_PATTERN = IN_APP_NOTIFICATION_ALLOWED_HTML_TAGS.join("|");
const DISALLOWED_TAG_REGEX = new RegExp(
  `<(?!(?:\\/?)(?:${ALLOWED_TAG_PATTERN})\\b)[^>]+>`,
  "gi",
);
const LINE_BREAK_BLOCK_TAGS = new Set(["div", "p"]);

const hasVisiblePreviousSibling = (node) => {
  let sibling = node.previousSibling;
  while (sibling) {
    if (sibling.nodeType === Node.ELEMENT_NODE) return true;
    if (
      sibling.nodeType === Node.TEXT_NODE
      && sibling.textContent.replace(/\u00a0/g, " ").trim()
    ) {
      return true;
    }
    sibling = sibling.previousSibling;
  }
  return false;
};

const nodeStartsWithBreak = (node) => {
  let child = node.firstChild;
  while (child) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      return child.tagName.toLowerCase() === "br";
    }
    if (
      child.nodeType === Node.TEXT_NODE
      && child.textContent.replace(/\u00a0/g, " ").trim()
    ) {
      return false;
    }
    child = child.nextSibling;
  }
  return false;
};

const ALLOWED_FONT_SIZE_VALUES = new Set([
  "small",
  "medium",
  "large",
  "x-small",
  "x-large",
  "smaller",
  "larger",
]);

const isSafeColorValue = (value) =>
  /^#[0-9a-f]{3,8}$/i.test(value) ||
  /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i.test(value) ||
  /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\)$/i.test(value);

const sanitizeInlineStyle = (styleStr) => {
  if (!styleStr?.trim()) return null;

  const safeDeclarations = styleStr
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const colonIndex = part.indexOf(":");
      if (colonIndex === -1) return null;
      const property = part.slice(0, colonIndex).trim().toLowerCase();
      const value = part.slice(colonIndex + 1).trim().toLowerCase();
      if (!property || !value) return null;

      if (property === "color" && isSafeColorValue(value)) {
        return `color:${value}`;
      }
      if (property === "background-color" && isSafeColorValue(value)) {
        return `background-color:${value}`;
      }
      if (property === "font-size" && ALLOWED_FONT_SIZE_VALUES.has(value)) {
        return `font-size:${value}`;
      }
      return null;
    })
    .filter(Boolean);

  return safeDeclarations.length ? safeDeclarations.join(";") : null;
};

export const sanitizeInAppNotificationHtml = (html = "") => {
  if (!html?.trim()) return "";

  if (typeof DOMParser === "undefined") {
    return html.replace(DISALLOWED_TAG_REGEX, "").trim();
  }

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return "";

  const sanitizeNode = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const tag = child.tagName.toLowerCase();
      if (!ALLOWED_HTML_TAG_SET.has(tag)) {
        sanitizeNode(child);
        const parent = child.parentNode;
        if (!parent) return;

        if (
          LINE_BREAK_BLOCK_TAGS.has(tag)
          && hasVisiblePreviousSibling(child)
          && !nodeStartsWithBreak(child)
        ) {
          parent.insertBefore(doc.createElement("br"), child);
        }

        while (child.firstChild) {
          parent.insertBefore(child.firstChild, child);
        }
        parent.removeChild(child);
        return;
      }

      if (tag === "span") {
        const safeStyle = sanitizeInlineStyle(child.getAttribute("style"));
        [...child.attributes].forEach((attr) => child.removeAttribute(attr.name));
        if (safeStyle) {
          child.setAttribute("style", safeStyle);
        } else if (!child.textContent?.trim() && !child.querySelector("*")) {
          child.parentNode?.removeChild(child);
          return;
        }
        sanitizeNode(child);
        return;
      }

      [...child.attributes].forEach((attr) => child.removeAttribute(attr.name));
      sanitizeNode(child);
    });
  };

  sanitizeNode(root);
  return root.innerHTML.trim();
};

/** Extract editor fragment from legacy full-document HTML stored before Base64 encoding. */
const unwrapInAppNotificationHtmlDocument = (html = "") => {
  const raw = String(html ?? "").trim();
  if (!raw) return "";

  const looksLikeDocument =
    /^\s*<!DOCTYPE/i.test(raw) ||
    /<html[\s>]/i.test(raw) ||
    /<body[\s>]/i.test(raw);

  if (!looksLikeDocument) {
    return raw;
  }

  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    const fromBody = doc.body?.innerHTML?.trim();
    if (fromBody) {
      return fromBody;
    }
  }

  const bodyMatch = raw.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch?.[1] != null) {
    return bodyMatch[1].trim();
  }

  return raw;
};

const IN_APP_NOTIFICATION_HTML_DOCUMENT_PREFIX = "<!DOCTYPE html><html><body>";
const IN_APP_NOTIFICATION_HTML_DOCUMENT_SUFFIX = "</body></html>";

const wrapInAppNotificationBodyHtml = (innerHtml = "") => {
  const inner = String(innerHtml ?? "").trim();
  if (!inner) return "";
  const centered = `<center>${inner}</center>`;
  return `${IN_APP_NOTIFICATION_HTML_DOCUMENT_PREFIX}${centered}${IN_APP_NOTIFICATION_HTML_DOCUMENT_SUFFIX}`;
};

const unwrapInAppNotificationCenterHtml = (html = "") => {
  const raw = String(html ?? "").trim();
  if (!raw) return "";

  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, "text/html");
    const root = doc.body.firstChild;
    if (root?.nodeType === Node.ELEMENT_NODE && root.tagName.toLowerCase() === "center") {
      return root.innerHTML.trim();
    }
  }

  const centerMatch = raw.match(/^<center[^>]*>([\s\S]*?)<\/center>$/i);
  if (centerMatch?.[1] != null) {
    return centerMatch[1].trim();
  }

  return raw;
};

const unwrapInAppNotificationHtmlForForm = (html = "") =>
  unwrapInAppNotificationCenterHtml(unwrapInAppNotificationHtmlDocument(html));

const decodeInAppNotificationHtmlFromApi = (value = "") =>
  decodeRichTextHtmlFromApi(value, unwrapInAppNotificationHtmlForForm);

export const toExpiryPayload = (date) => {
  if (!date) return null;
  const ts = endOfDay(new Date(date)).getTime();
  if (Number.isNaN(ts)) return null;
  return ts;
};

const normalizeExpiryTimestamp = (value) => {
  const ts = Number(value);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  // Unix seconds (e.g. ~1.7e9) vs milliseconds (e.g. ~1.7e12).
  if (ts < 1e11) return ts * 1000;
  return ts;
};

export const parseExpiryValue = (expiry) => {
  if (expiry == null || expiry === "") return null;

  if (typeof expiry === "object") {
    if (expiry.$numberLong != null) {
      const ts = normalizeExpiryTimestamp(expiry.$numberLong);
      return ts == null ? null : new Date(ts);
    }

    if (expiry.$date != null) {
      const parsed = new Date(expiry.$date);
      return Number.isNaN(parsed.getTime()) || parsed.getTime() <= 0 ? null : parsed;
    }
  }

  if (typeof expiry === "number") {
    const ts = normalizeExpiryTimestamp(expiry);
    return ts == null ? null : new Date(ts);
  }

  if (typeof expiry === "string" && /^\d+$/.test(expiry.trim())) {
    const ts = normalizeExpiryTimestamp(expiry.trim());
    return ts == null ? null : new Date(ts);
  }

  const parsed = new Date(expiry);
  return Number.isNaN(parsed.getTime()) || parsed.getTime() <= 0 ? null : parsed;
};

export const resolveNotificationExpiry = (item) => {
  if (!item) return null;

  const candidates = [
    item.expiry,
    item.config?.expiry,
    item.notification?.expiry,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (parseExpiryValue(candidate)) return candidate;
  }

  return null;
};

export const formatInAppNotificationExpiry = (expiry) => {
  const date = parseExpiryValue(expiry);
  if (!date) return "—";
  try {
    return format(startOfDay(date), "dd MMM yyyy");
  } catch {
    return "—";
  }
};

export const isNotificationExpired = (expiry) => {
  const date = parseExpiryValue(expiry);
  if (!date) return false;
  return endOfDay(date).getTime() < Date.now();
};

export const getExpiryDateBounds = (referenceDate = new Date()) => {
  const minDate = startOfDay(referenceDate);
  const maxDate = endOfDay(
    addDays(minDate, IN_APP_NOTIFICATION_VALIDATIONS.expiryMaxDaysFromToday),
  );
  return { minDate, maxDate };
};

export const validateExpiryDateWindow = (expiryDate, referenceDate = new Date()) => {
  if (!expiryDate) return "Expiry date is required";

  const { minDate, maxDate } = getExpiryDateBounds(referenceDate);
  const selected = startOfDay(expiryDate);

  if (selected < minDate) {
    return "Expiry date cannot be in the past";
  }
  if (selected > maxDate) {
    return `Expiry date must be within ${IN_APP_NOTIFICATION_VALIDATIONS.expiryMaxDaysFromToday} days from today`;
  }
  return "";
};

export const validatePlatformField = (platform) => {
  if (!platform?.length) return "Select at least one platform";
  return "";
};

export const validateScreenTitleField = (title, { required = false } = {}) => {
  if (!String(title ?? "").trim()) {
    return required ? "Screen title is required" : "";
  }
  return "";
};

export const validateCarouselScreensField = (screens) => {
  if (!screens?.length) return "Add at least one carousel screen";
  return "";
};

export const mergeFieldErrors = (prevErrors, updates) => {
  const next = { ...prevErrors };
  Object.entries(updates).forEach(([key, value]) => {
    if (value) next[key] = value;
    else delete next[key];
  });
  return next;
};

export const reconcileCarouselErrors = (
  prevErrors,
  screens = [],
  { required = false } = {},
) => {
  const next = { ...prevErrors };
  const screensError = validateCarouselScreensField(screens);

  if (screensError) next.screens = screensError;
  else delete next.screens;

  screens.forEach((screen, index) => {
    const titleErr = validateScreenTitleField(screen.title, { required });
    const descErr = validateRichTextBodyField(screen.desc, {
      required,
      maxLength: IN_APP_NOTIFICATION_VALIDATIONS.screenDesc.maxLength,
    });
    const ctaErr = validateCtaField(screen.cta);
    const assetCustomPropertiesErr = validateAssetCustomProperties(
      screen.assets?.customProperties,
    );

    if (titleErr) next[`screenTitle_${index}`] = titleErr;
    else delete next[`screenTitle_${index}`];

    if (descErr) next[`screenDesc_${index}`] = descErr;
    else delete next[`screenDesc_${index}`];

    if (ctaErr) next[`screenCta_${index}`] = ctaErr;
    else delete next[`screenCta_${index}`];

    if (assetCustomPropertiesErr) {
      next[`screenAssetCustomProperties_${index}`] = assetCustomPropertiesErr;
    } else {
      delete next[`screenAssetCustomProperties_${index}`];
    }
  });

  Object.keys(next).forEach((key) => {
    const match = key.match(
      /^screen(Title|Desc|Cta|AssetCustomProperties)_(\d+)$/,
    );
    if (match && Number(match[2]) >= screens.length) {
      delete next[key];
    }
  });

  return next;
};

export const clampNotificationTitle = (value = "") =>
  String(value ?? "").slice(0, IN_APP_NOTIFICATION_VALIDATIONS.title.maxLength);

export const getNotificationTitleLimitMessage = (
  maxLength = IN_APP_NOTIFICATION_VALIDATIONS.title.maxLength,
) => `You've reached the maximum of ${maxLength} characters for the title.`;

export const getNotificationDescriptionLimitMessage = (
  maxLength = IN_APP_NOTIFICATION_VALIDATIONS.body.maxLength,
) =>
  `You've reached the maximum of ${maxLength} characters for the description. Spaces and formatting (bold, line breaks) also count toward this limit.`;

export const validateTitleField = (title, { required = false } = {}) => {
  const raw = String(title ?? "");
  const trimmed = raw.trim();
  const maxLength = IN_APP_NOTIFICATION_VALIDATIONS.title.maxLength;

  if (raw.length > maxLength) {
    return getNotificationTitleLimitMessage(maxLength);
  }
  if (!trimmed) {
    return required ? "Title is required" : "";
  }
  return "";
};

export const getNotificationTitleHelperText = (title, { validationError = "" } = {}) => {
  const maxLength = IN_APP_NOTIFICATION_VALIDATIONS.title.maxLength;
  const length = String(title ?? "").length;

  if (validationError) {
    return { text: validationError, tone: "error" };
  }
  return { text: `${length}/${maxLength} characters`, tone: "default" };
};

export const getNotificationRichTextHelperText = (
  html,
  {
    validationError = "",
    maxLength = IN_APP_NOTIFICATION_VALIDATIONS.body.maxLength,
  } = {},
) => {
  const length = getNotificationBodyPayloadLength(html);

  if (validationError) {
    return { text: validationError, tone: "error" };
  }
  return { text: `${length}/${maxLength} characters`, tone: "default" };
};

export const validateMaxPerDayField = (value, { required = false } = {}) => {
  if (value === "" || value == null) {
    return required ? "Max per day is required" : "";
  }

  const maxPerDay = Number(value);
  if (!Number.isFinite(maxPerDay)) {
    return required ? "Max per day is required" : "";
  }
  if (maxPerDay < IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerDay.min) {
    return `Max per day must be at least ${IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerDay.min}`;
  }
  if (maxPerDay > IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerDay.max) {
    return `Max per day cannot exceed ${IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerDay.max}`;
  }
  return "";
};

export const validateMaxPerWeekField = (value, { required = false } = {}) => {
  if (value === "" || value == null) {
    return required ? "Max per week is required" : "";
  }

  const maxPerWeek = Number(value);
  if (!Number.isFinite(maxPerWeek)) {
    return required ? "Max per week is required" : "";
  }
  if (maxPerWeek < IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerWeek.min) {
    return `Max per week must be at least ${IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerWeek.min}`;
  }
  if (maxPerWeek > IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerWeek.max) {
    return `Max per week cannot exceed ${IN_APP_NOTIFICATION_VALIDATIONS.configMaxPerWeek.max}`;
  }
  return "";
};

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, "");

const normalizeNotificationTypeLabel = (type) => {
  const normalized = String(type || "").trim().toLowerCase();
  return normalized === "carousel" ? "Carousel" : "Card";
};

export const toApiNotificationType = (type) => normalizeNotificationTypeLabel(type);

export const fromApiNotificationType = (type) => normalizeNotificationTypeLabel(type);

export const isCardNotificationType = (type) =>
  fromApiNotificationType(type) === "Card";

export const isValidCtaTarget = (target) =>
  Boolean(
    target?.trim().startsWith(IN_APP_NOTIFICATION_CTA_TARGET_PREFIX),
  );

export const getPlainTextLength = (html = "") => stripHtml(html).trim().length;

/** Length of sanitized HTML sent in the API body field (matches backend validation). */
export const getNotificationBodyPayloadLength = (html = "") =>
  sanitizeInAppNotificationHtml(html).length;

export const clampNotificationBodyHtml = (
  html = "",
  maxLength = IN_APP_NOTIFICATION_VALIDATIONS.body.maxLength,
) => {
  let sanitized = sanitizeInAppNotificationHtml(html);
  if (!sanitized || sanitized.length <= maxLength) {
    return sanitized;
  }

  if (typeof DOMParser === "undefined") {
    return stripHtml(sanitized).trim().slice(0, maxLength);
  }

  const doc = new DOMParser().parseFromString(`<div>${sanitized}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return "";

  const textNodes = [];
  const collectTextNodes = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      textNodes.push(node);
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      [...node.childNodes].forEach(collectTextNodes);
    }
  };
  collectTextNodes(root);

  let remaining = maxLength;
  let truncateAtNode = null;

  textNodes.forEach((textNode) => {
    if (truncateAtNode) {
      textNode.remove();
      return;
    }

    const text = textNode.textContent ?? "";
    if (text.length <= remaining) {
      remaining -= text.length;
      return;
    }

    textNode.textContent = text.slice(0, remaining);
    truncateAtNode = textNode;
  });

  if (truncateAtNode) {
    let current = truncateAtNode;
    while (current && current !== root) {
      let sibling = current.nextSibling;
      while (sibling) {
        const next = sibling.nextSibling;
        sibling.remove();
        sibling = next;
      }
      current = current.parentNode;
    }
    let sibling = root.nextSibling;
    while (sibling) {
      const next = sibling.nextSibling;
      sibling.remove();
      sibling = next;
    }
  }

  const pruneEmptyElements = (node) => {
    [...node.childNodes].forEach(pruneEmptyElements);
    if (node !== root && node.nodeType === Node.ELEMENT_NODE && !node.textContent?.length) {
      node.remove();
    }
  };
  pruneEmptyElements(root);

  sanitized = root.innerHTML;

  while (sanitized.length > maxLength && getPlainTextLength(sanitized) > 0) {
    const plainText = stripHtml(sanitized);
    const nextPlainText = plainText.slice(0, Math.max(0, plainText.length - 1));
    sanitized = sanitizeInAppNotificationHtml(nextPlainText);
  }

  return sanitized.length <= maxLength ? sanitized : sanitized.slice(0, maxLength);
};

const prepareInAppNotificationTitleForApi = (title = "") => {
  const text = clampNotificationTitle(title);
  if (!text) return "";
  return encodeTextForApi(text);
};

const prepareInAppNotificationTitleForForm = (title = "") =>
  clampNotificationTitle(decodePlainTextFromApi(title));

const prepareInAppNotificationHtmlForApi = (
  html = "",
  maxLength = IN_APP_NOTIFICATION_VALIDATIONS.body.maxLength,
) => {
  const inner = clampNotificationBodyHtml(
    sanitizeInAppNotificationHtml(html),
    maxLength,
  );
  if (!inner) return "";
  return encodeTextForApi(wrapInAppNotificationBodyHtml(inner));
};

const prepareInAppNotificationHtmlForForm = (
  html = "",
  maxLength = IN_APP_NOTIFICATION_VALIDATIONS.body.maxLength,
) =>
  clampNotificationBodyHtml(
    sanitizeInAppNotificationHtml(decodeInAppNotificationHtmlFromApi(html)),
    maxLength,
  );

export const normalizeTargetTypeFromApi = (targetType) => {
  const normalized = String(targetType || "").trim().toLowerCase();
  if (normalized === "user" || normalized === "user-specific") {
    return "user-specific";
  }
  return "broadcast";
};

export const toApiTargetType = (targetType) => {
  const normalized = normalizeTargetTypeFromApi(targetType);
  return normalized === "user-specific" ? "user-specific" : "broadcast";
};

/** Maps UI list filter to GET /notification/inapp targetType query param. */
export const toApiListTargetTypeParam = (targetType) => {
  const normalized = String(targetType || "").trim().toLowerCase();
  if (normalized === "all") return "all";
  if (normalized === "user-specific" || normalized === "user") return "user-specific";
  return "broadcast";
};

export const matchesTargetType = (itemTargetType, filterTargetType) => {
  if (!filterTargetType) return true;
  return normalizeTargetTypeFromApi(itemTargetType) === normalizeTargetTypeFromApi(filterTargetType);
};

export const isValidInAppNotificationListItem = (item) =>
  Boolean(String(item?.notificationId || item?.id || "").trim());

export const isUserSpecificNotification = (targetType) =>
  normalizeTargetTypeFromApi(targetType) === "user-specific";

export const parseTargetUserCount = (value) => {
  if (value == null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const extractTargetUserCountFromItem = (item) =>
  parseTargetUserCount(item?.totalUserCount);

export const sumTargetUserCounts = (items = []) =>
  items.reduce(
    (total, item) => total + (extractTargetUserCountFromItem(item) ?? 0),
    0,
  );

export const mapApiListItem = (item) => {
  if (!item) return item;

  const id = String(item.notificationId || item.id || "");
  const screens = (Array.isArray(item.screens) ? item.screens : []).map((screen) => ({
    ...screen,
    title: prepareInAppNotificationTitleForForm(screen.title || ""),
  }));

  return {
    ...item,
    id,
    notificationId: id,
    title: prepareInAppNotificationTitleForForm(item.title || ""),
    targetType: normalizeTargetTypeFromApi(item.targetType),
    type: fromApiNotificationType(item.type),
    platform: normalizePlatformValues(item.platform),
    screens,
    cta: Array.isArray(item.cta) ? item.cta : [],
    assets: item.assets || undefined,
    config: item.config || {},
    expiry: resolveNotificationExpiry(item),
    targetUserCount: extractTargetUserCountFromItem(item),
  };
};

export const parseAssetMetadataResponse = (data) => {
  const entries = Array.isArray(data) ? data : [];

  return entries
    .map((item) => ({
      tag: item?.tag?.trim() || "",
      resolutions: Array.isArray(item?.resolutions)
        ? item.resolutions.map(String).filter(Boolean)
        : [],
    }))
    .filter((item) => item.tag || item.resolutions.length);
};

export const mapMetadataEntriesToAssetTags = (entries = []) =>
  entries
    .map((entry) => ({
      assetTag: entry?.tag?.trim() || "",
      resolutions: Array.isArray(entry?.resolutions)
        ? entry.resolutions.map(String).filter(Boolean)
        : [],
    }))
    .filter((item) => item.assetTag);

export const normalizeApiAssetTags = (assets) => {
  if (Array.isArray(assets?.tags) && assets.tags.length) {
    return assets.tags
      .map((item) => ({
        assetTag: String(item?.assetTag || item?.tag || "").trim(),
        resolutions: Array.isArray(item?.resolutions)
          ? item.resolutions.map(String).filter(Boolean)
          : [],
      }))
      .filter((item) => item.assetTag);
  }

  if (assets?.assetTag && String(assets.assetTag).trim()) {
    return [
      {
        assetTag: String(assets.assetTag).trim(),
        resolutions: Array.isArray(assets?.resolutions)
          ? assets.resolutions.map(String).filter(Boolean)
          : [],
      },
    ];
  }

  return [];
};

const getFormAssetId = (assets) =>
  assets?.asset?.trim() || assets?.assetId?.trim() || "";

const RESERVED_ASSET_PAYLOAD_KEYS = new Set([
  "asset",
  "assetId",
  "assetTag",
  "resolutions",
  "tags",
  "customProperties",
  "additionalProperties",
  "url",
  "id",
]);

export const normalizeAssetCustomProperties = (customProperties = []) => {
  const maxPairs = IN_APP_NOTIFICATION_VALIDATIONS.maxAssetCustomProperties;
  const pairs = Array.isArray(customProperties) ? customProperties : [];

  return pairs.slice(0, maxPairs).map((pair) => ({
    key: String(pair?.key ?? ""),
    value: String(pair?.value ?? ""),
  }));
};

const hasFilledCustomProperty = (pair = {}) =>
  Boolean(String(pair?.key ?? "").trim() && String(pair?.value ?? "").trim());

export const validateAssetCustomPropertyValue = (value = "") => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";

  if (trimmed.includes(",")) {
    return "Enter only one value. Comma-separated values are not allowed";
  }

  return "";
};

export const validateAssetCustomProperties = (customProperties = []) => {
  const pairs = normalizeAssetCustomProperties(customProperties);

  for (let index = 0; index < pairs.length; index += 1) {
    const key = pairs[index].key.trim();
    const value = pairs[index].value.trim();

    if (key && !value) {
      return `Value is required when Key ${index + 1} is entered`;
    }
    if (value && !key) {
      return `Key is required when Value ${index + 1} is entered`;
    }

    const valueError = validateAssetCustomPropertyValue(value);
    if (valueError) {
      return `Value ${index + 1}: ${valueError}`;
    }
  }

  return "";
};

export const extractApiCustomProperties = (assets = {}) => {
  if (!assets || typeof assets !== "object" || Array.isArray(assets)) {
    return [];
  }

  const fromExplicitArray = [
    ...(Array.isArray(assets.customProperties) ? assets.customProperties : []),
    ...(Array.isArray(assets.additionalProperties)
      ? assets.additionalProperties
      : []),
  ]
    .map((pair) => ({
      key: String(pair?.key ?? pair?.name ?? "").trim(),
      value: String(pair?.value ?? "").trim(),
    }))
    .filter(hasFilledCustomProperty);

  if (fromExplicitArray.length) {
    return normalizeAssetCustomProperties(fromExplicitArray);
  }

  const pairs = Object.entries(assets)
    .filter(([key]) => !RESERVED_ASSET_PAYLOAD_KEYS.has(String(key)))
    .filter(([, value]) => {
      if (value == null) return false;
      if (typeof value === "object") return false;
      return String(value).trim() !== "";
    })
    .slice(0, IN_APP_NOTIFICATION_VALIDATIONS.maxAssetCustomProperties)
    .map(([key, value]) => ({
      key: String(key).trim(),
      value: String(value).trim(),
    }))
    .filter(hasFilledCustomProperty);

  return normalizeAssetCustomProperties(pairs);
};

const resolveApiAssets = (source = {}) =>
  source?.assets || source?.config?.assets || undefined;

const mergeAssetCustomProperties = (payload, customProperties = []) => {
  normalizeAssetCustomProperties(customProperties).forEach((pair) => {
    const key = pair.key.trim();
    const value = pair.value.trim();

    if (!key || !value || RESERVED_ASSET_PAYLOAD_KEYS.has(key)) {
      return;
    }

    payload[key] = value;
  });
};

const cleanAssets = (assets) => {
  const asset = getFormAssetId(assets);
  if (!asset) return undefined;
  const payload = { asset };

  const assetTag = assets?.assetTag?.trim();
  if (assetTag) {
    payload.assetTag = assetTag;
  }

  const resolutions = Array.isArray(assets?.resolutions)
    ? assets.resolutions.map(String).filter(Boolean)
    : [];
  if (resolutions.length) {
    payload.resolutions = resolutions;
  }

  // Flatten optional key/value pairs into the assets object, e.g. assetType: "image"
  mergeAssetCustomProperties(
    payload,
    normalizeAssetCustomProperties(assets?.customProperties),
  );

  return payload;
};

const mapApiAssetsToForm = (assets) => {
  const source = assets && typeof assets === "object" ? assets : {};
  const tags = normalizeApiAssetTags(source);

  return {
    asset: source?.assetId || source?.asset || "",
    assetTag: tags[0]?.assetTag || "",
    resolutions: tags[0]?.resolutions || [],
    tags,
    customProperties: extractApiCustomProperties(source),
  };
};

const cleanCtas = (ctas = []) =>
  ctas
    .filter((cta) => cta?.label?.trim() && cta?.target?.trim())
    .map((cta, index) => ({
      id: (cta.id?.trim() || `cta${index + 1}`).slice(
        0,
        IN_APP_NOTIFICATION_VALIDATIONS.ctaId.maxLength,
      ),
      label: cta.label.trim().slice(0, IN_APP_NOTIFICATION_VALIDATIONS.ctaLabel.maxLength),
      target: cta.target.trim().slice(0, IN_APP_NOTIFICATION_VALIDATIONS.ctaTarget.maxLength),
    }));

const cleanCardCtas = cleanCtas;

const cleanConfigureCtas = (ctas = []) =>
  ctas
    .filter((cta) => cta?.label?.trim() && cta?.target?.trim())
    .map((cta) => ({
      label: cta.label.trim().slice(0, IN_APP_NOTIFICATION_VALIDATIONS.ctaLabel.maxLength),
      target: cta.target.trim().slice(0, IN_APP_NOTIFICATION_VALIDATIONS.ctaTarget.maxLength),
    }));

const applyOptionalAssetsField = (target, assets) => {
  const cleaned = cleanAssets(assets);
  if (cleaned) {
    target.assets = cleaned;
  }
};

const applyOptionalCtaField = (target, ctas) => {
  const cleaned = cleanCardCtas(ctas);
  if (cleaned.length) {
    target.cta = cleaned;
  }
};

const parseOptionalConfigLimit = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const buildOptionalConfigPayload = (config = {}) => {
  const payload = {};
  const maxPerDay = parseOptionalConfigLimit(config.maxPerDay);
  const maxPerWeek = parseOptionalConfigLimit(config.maxPerWeek);

  if (maxPerDay != null) payload.maxPerDay = maxPerDay;
  if (maxPerWeek != null) payload.maxPerWeek = maxPerWeek;

  return Object.keys(payload).length ? payload : undefined;
};

export const buildApiPayload = (form, { isUpdate = false } = {}) => {
  const apiType = toApiNotificationType(form.type);
  const expiry = toExpiryPayload(form.expiryDate);
  const payload = {
    title: prepareInAppNotificationTitleForApi(form.title),
    body: prepareInAppNotificationHtmlForApi(form.body),
    expiry,
  };

  const configPayload = buildOptionalConfigPayload(form.config);
  if (configPayload) {
    payload.config = configPayload;
  }

  if (!isUpdate) {
    payload.platform = normalizePlatformValues(form.platform);
    payload.type = apiType;
    payload.targetType = toApiTargetType(form.targetType);
  }

  if (form.type === "Card") {
    applyOptionalAssetsField(payload, form.assets);
    applyOptionalCtaField(payload, form.cta);
  } else if (form.type === "Carousel") {
    payload.screens = (form.screens || []).map((screen) => {
      const screenPayload = {
        title: prepareInAppNotificationTitleForApi(screen.title),
        desc: prepareInAppNotificationHtmlForApi(
          screen.desc,
          IN_APP_NOTIFICATION_VALIDATIONS.screenDesc.maxLength,
        ),
      };
      applyOptionalAssetsField(screenPayload, screen.assets);
      applyOptionalCtaField(screenPayload, screen.cta);
      return screenPayload;
    });
  }

  return payload;
};

const mapCtaFromApi = (ctaList = []) =>
  (Array.isArray(ctaList) ? ctaList : []).map((cta) => ({
    id: cta.id || "",
    label: cta.label || "",
    target: cta.target || "",
  }));

const hasFilledCta = (ctas = []) =>
  ctas.some((cta) => cta?.label?.trim() || cta?.target?.trim());

const resolveFormCtas = (apiCtas, defaultEmptyCta) => {
  const mapped = mapCtaFromApi(apiCtas);
  if (hasFilledCta(mapped)) return mapped;
  return defaultEmptyCta ? createDefaultCtaList() : [];
};

export const mapApiToForm = (data, { defaultEmptyCta = true } = {}) => {
  const item = mapApiListItem(data);

  return {
    id: item.id || "",
    title: item.title || "",
    body: prepareInAppNotificationHtmlForForm(item.body || ""),
    targetType: item.targetType,
    type: fromApiNotificationType(item.type),
    platform: normalizePlatformValues(item.platform),
    expiryDate: (() => {
      const date = parseExpiryValue(item.expiry);
      return date ? startOfDay(date) : null;
    })(),
    config: {
      maxPerDay: item.config?.maxPerDay ?? "",
      maxPerWeek: item.config?.maxPerWeek ?? "",
    },
    assets: mapApiAssetsToForm(resolveApiAssets(item)),
    cta: resolveFormCtas(item.cta, defaultEmptyCta),
    screens: item.screens?.length
      ? item.screens.map((screen) => ({
          title: screen.title || "",
          desc: prepareInAppNotificationHtmlForForm(
            screen.desc || "",
            IN_APP_NOTIFICATION_VALIDATIONS.screenDesc.maxLength,
          ),
          assets: mapApiAssetsToForm(resolveApiAssets(screen)),
          cta: resolveFormCtas(screen.cta, defaultEmptyCta),
        }))
      : [],
  };
};

export const validateRichTextRequired = (html) => Boolean(stripHtml(html).trim());

export const validateRichTextBodyField = (
  html,
  {
    required = false,
    maxLength = IN_APP_NOTIFICATION_VALIDATIONS.body.maxLength,
  } = {},
) => {
  const payloadLength = getNotificationBodyPayloadLength(html);

  if (payloadLength > maxLength) {
    return getNotificationDescriptionLimitMessage(maxLength);
  }
  if (!validateRichTextRequired(html)) {
    return required ? "Description is required" : "";
  }
  return "";
};

const validateCtaList = (ctas = [], errorKey, errors, { required = false } = {}) => {
  if (errors[errorKey]) return;

  for (let index = 0; index < (ctas || []).length; index += 1) {
    const cta = ctas[index];
    const hasLabel = Boolean(cta?.label?.trim());
    const hasTarget = Boolean(cta?.target?.trim());
    if (hasLabel !== hasTarget) {
      errors[errorKey] = "Complete both label and target for each CTA, or remove the row";
      return;
    }
  }

  const filledCtas = (ctas || []).filter(
    (cta) => cta?.label?.trim() && cta?.target?.trim(),
  );

  if (!filledCtas.length) {
    return;
  }

  for (let index = 0; index < filledCtas.length; index += 1) {
    const cta = filledCtas[index];
    if (!isValidCtaTarget(cta.target)) {
      errors[errorKey] = `Target must start with ${IN_APP_NOTIFICATION_CTA_TARGET_PREFIX}`;
      return;
    }
    if (cta.label.trim().length > IN_APP_NOTIFICATION_VALIDATIONS.ctaLabel.maxLength) {
      errors[errorKey] =
        `CTA label must be ${IN_APP_NOTIFICATION_VALIDATIONS.ctaLabel.maxLength} characters or less`;
      return;
    }
    if (cta.target.trim().length > IN_APP_NOTIFICATION_VALIDATIONS.ctaTarget.maxLength) {
      errors[errorKey] =
        `CTA target must be ${IN_APP_NOTIFICATION_VALIDATIONS.ctaTarget.maxLength} characters or less`;
      return;
    }
    const ctaId = cta.id?.trim() || `cta${index + 1}`;
    if (ctaId.length > IN_APP_NOTIFICATION_VALIDATIONS.ctaId.maxLength) {
      errors[errorKey] =
        `CTA id must be ${IN_APP_NOTIFICATION_VALIDATIONS.ctaId.maxLength} characters or less`;
      return;
    }
  }
};

export const validateCtaField = (ctas = []) => {
  const ctaErrors = {};
  validateCtaList(ctas, "cta", ctaErrors);
  return ctaErrors.cta || "";
};

export const validateNotificationForm = (formData) => {
  const errors = {};

  const titleError = validateTitleField(formData.title, { required: true });
  if (titleError) errors.title = titleError;
  const bodyError = validateRichTextBodyField(formData.body, { required: true });
  if (bodyError) errors.body = bodyError;
  if (!formData.platform?.length) errors.platform = "Select at least one platform";

  const expiryError = validateExpiryDateWindow(formData.expiryDate);
  if (expiryError) errors.expiryDate = expiryError;

  if (!formData.targetType) errors.targetType = "Target type is required";
  if (!formData.type) errors.type = "Notification type is required";

  const maxPerDayError = validateMaxPerDayField(formData.config?.maxPerDay);
  if (maxPerDayError) errors.maxPerDay = maxPerDayError;

  const maxPerWeekError = validateMaxPerWeekField(formData.config?.maxPerWeek);
  if (maxPerWeekError) errors.maxPerWeek = maxPerWeekError;

  if (formData.type === "Card") {
    validateCtaList(formData.cta, "cta", errors);
    const assetCustomPropertiesError = validateAssetCustomProperties(
      formData.assets?.customProperties,
    );
    if (assetCustomPropertiesError) {
      errors.assetCustomProperties = assetCustomPropertiesError;
    }
  }

  if (formData.type === "Carousel") {
    if (!formData.screens?.length) {
      errors.screens = "Add at least one carousel screen";
    }
    formData.screens?.forEach((screen, index) => {
      if (!screen.title?.trim()) {
        errors[`screenTitle_${index}`] = "Screen title is required";
      }
      const screenDescError = validateRichTextBodyField(screen.desc, {
        required: true,
        maxLength: IN_APP_NOTIFICATION_VALIDATIONS.screenDesc.maxLength,
      });
      if (screenDescError) {
        errors[`screenDesc_${index}`] = screenDescError;
      }
      validateCtaList(screen.cta, `screenCta_${index}`, errors);

      const screenAssetCustomPropertiesError = validateAssetCustomProperties(
        screen.assets?.customProperties,
      );
      if (screenAssetCustomPropertiesError) {
        errors[`screenAssetCustomProperties_${index}`] =
          screenAssetCustomPropertiesError;
      }
    });
  }

  return errors;
};

export const isAssignmentPermissionError = (error) => {
  const status = error?.response?.status;
  const message = (
    error?.response?.data?.message ||
    error?.message ||
    ""
  ).toLowerCase();

  return status === 403 || message.includes("insufficient permission");
};

const parseBracketList = (raw = "") =>
  raw
    .split(",")
    .map((item) => item.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean);

export const formatInvalidAssignmentUserIdsMessage = (invalidIds = []) => {
  const ids = invalidIds.filter(Boolean);
  if (!ids.length) {
    return "One or more user IDs are invalid. Please verify and try again.";
  }
  if (ids.length === 1) {
    return `Invalid user ID: ${ids[0]}. Please verify the user ID and try again.`;
  }
  return `Invalid user IDs: ${ids.join(", ")}. Please verify and try again.`;
};

export const formatInvalidAssignmentMobileNumbersMessage = (invalidNumbers = []) => {
  const numbers = invalidNumbers.filter(Boolean);
  if (!numbers.length) {
    return "One or more mobile numbers are invalid. Please verify and try again.";
  }
  if (numbers.length === 1) {
    return `Invalid mobile number: ${numbers[0]}. Please verify and try again.`;
  }
  return `Invalid mobile numbers: ${numbers.join(", ")}. Please verify and try again.`;
};

export const parseInvalidAssignmentMessage = (message) => {
  if (typeof message !== "string" || !message.trim()) return null;

  const userMatch = message.match(/invalid userIds?:\s*\[(.*)\]/i);
  if (userMatch) {
    return formatInvalidAssignmentUserIdsMessage(parseBracketList(userMatch[1]));
  }

  const mobileMatch = message.match(/invalid mobile(?:No|Numbers?)?:\s*\[(.*)\]/i);
  if (mobileMatch) {
    return formatInvalidAssignmentMobileNumbersMessage(parseBracketList(mobileMatch[1]));
  }

  return null;
};

export const getAssignmentErrorMessage = (error, fallback = "Request failed") => {
  if (isAssignmentPermissionError(error)) {
    return ASSIGNMENT_PERMISSION_ERROR_MESSAGE;
  }

  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map(String).join(", ");
  }
  if (data?.validationErrors && typeof data.validationErrors === "object") {
    return Object.values(data.validationErrors).flat().map(String).join(", ");
  }

  const rawMessage = data?.message || error?.message || fallback;
  return parseInvalidAssignmentMessage(rawMessage) || rawMessage;
};

export const validateAssignmentForm = ({
  selectedNotificationId = "",
  targetBy = "userIds",
  userIds = [],
  mobileNumbers = [],
  canUploadUserIds = false,
  canUploadMobile = false,
}) => {
  const errors = {};

  if (!selectedNotificationId?.trim()) {
    errors.notification = "Select a user-specific notification";
  }

  if (targetBy === "userIds") {
    if (!userIds.length) {
      errors.targets = "Add at least one user ID";
    } else if (!canUploadUserIds) {
      errors.targets = "You do not have permission to add user IDs";
    }
  } else if (!mobileNumbers.length) {
    errors.targets = "Add at least one mobile number";
  } else if (!canUploadMobile) {
    errors.targets = "You do not have permission to add mobile numbers";
  }

  return errors;
};

export const buildConfigurePayload = ({
  userIds = [],
  mobileNo = [],
  overrideEnabled = false,
  overrideTitle,
  overrideBody,
  overrideExpiryDate,
  notificationType,
  overrideAssets,
  overrideCta = [],
  overrideScreens = [],
  fallbackTitle = "",
  fallbackBody = "",
  fallbackExpiry,
}) => {
  const payload = {};

  const cleanedUserIds = userIds.filter(Boolean);
  const cleanedMobile = mobileNo.filter(Boolean);

  if (cleanedUserIds.length) payload.userIDs = cleanedUserIds;
  if (cleanedMobile.length) payload.mobileNo = cleanedMobile;

  if (!overrideEnabled) {
    return payload;
  }
  const config = {
    title: prepareInAppNotificationTitleForApi(overrideTitle || fallbackTitle || ""),
    body: prepareInAppNotificationHtmlForApi(overrideBody || fallbackBody || ""),
  };

  const expiryPayload = overrideExpiryDate
    ? toExpiryPayload(overrideExpiryDate)
    : parseExpiryValue(fallbackExpiry)?.getTime() ?? null;

  if (expiryPayload != null) {
    config.expiry = expiryPayload;
  }

  if (fromApiNotificationType(notificationType) === "Carousel") {
    if (overrideScreens?.length) {
      config.screens = overrideScreens.map((screen) => {
        const screenPayload = {
          title: prepareInAppNotificationTitleForApi(screen.title),
          desc: prepareInAppNotificationHtmlForApi(
            screen.desc || "",
            IN_APP_NOTIFICATION_VALIDATIONS.screenDesc.maxLength,
          ),
        };
        const assets = cleanAssets(screen.assets);
        if (assets) screenPayload.assets = assets;
        const cta = cleanConfigureCtas(screen.cta);
        if (cta.length) screenPayload.cta = cta;
        return screenPayload;
      });
    }
  } else {
    const assets = cleanAssets(overrideAssets);
    if (assets) config.assets = assets;
    const cta = cleanConfigureCtas(overrideCta);
    if (cta.length) {
      config.cta = cta;
    }
  }

  payload.config = config;
  return payload;
};
